import { ownEntries } from '#library/primitive.library.js';
import { isDefined, isPrimitive } from '#library/assertion.library.js';
import { sym } from '#library/symbol.library.js';
/** General utility functions */
/**
 * Analyzes the Call Stack to determine the calling function's name.
 *
 * @returns The name of the calling function, or undefined if unresolvable
 * @example
 * ```ts
 * function myFunc() { console.log(getCaller()); } // 'myFunc'
 * ```
 */
export const getCaller = () => {
    const stackTrace = new Error().stack // only tested in latest FF and Chrome
        ?.split('\n')
        ?.map(itm => itm.trim())
        ?.filter(itm => !itm.startsWith('Error'))
        ?? [];
    const callerName = stackTrace[2].split(' ');
    return (callerName[1] === 'new') ? callerName[2] : callerName[1].split('.')[0];
};
/**
 * Analyzes the Call Stack to determine the calling script's URI.
 *
 * @param nbr - The stack depth to inspect (default: 1)
 * @returns The URI of the calling script
 * @example
 * ```ts
 * const scriptUrl = getScript();
 * ```
 */
export const getScript = (nbr = 1) => {
    const stackTrace = new Error().stack
        ?.match(/([^ \n\(@])*([a-z]*:\/\/\/?)*?[a-z0-9\/\\]*\.js/ig)?.[nbr];
    return decodeURI(stackTrace ?? ''); // decodeURI is needed to handle spaces in file-names
};
/**
 * Introduces a wait-timer that will reject with an Error on timeout.
 * Best used with `Promise.race([myTask(), sleep()])`.
 *
 * @param msg - The string to display on a timeout (default: 'sleep: timed out')
 * @param timeout - The number of milliseconds to sleep (default: 2000)
 * @returns A Promise that rejects after the specified timeout
 * @example
 * ```ts
 * await Promise.race([fetchData(), sleep('Fetch timeout', 5000)]);
 * ```
 */
export const sleep = (msg = 'sleep: timed out', timeout = 2000) => new Promise((_, reject) => setTimeout(() => reject(new Error(msg)), timeout));
/** Javascript Runtimes */
export const CONTEXT = {
    'Unknown': 'unknown',
    'Browser': 'browser',
    'NodeJS': 'nodejs',
    'Deno': 'deno',
    'GoogleAppsScript': 'google-apps-script',
};
/**
 * Determines the current JavaScript environment context.
 * Useful for branching logic based on the runtime environment.
 *
 * @returns An object containing the global scope reference and the CONTEXT type enum
 * @example
 * ```ts
 * const { type } = getContext();
 * if (type === CONTEXT.Browser) { ... }
 * ```
 */
export const getContext = () => {
    const global = globalThis;
    if (isDefined(global.SpreadsheetApp))
        return { global, type: CONTEXT.GoogleAppsScript };
    if (isDefined(global.window?.document))
        return { global, type: CONTEXT.Browser };
    if (isDefined(global.Deno))
        return { global, type: CONTEXT.Deno };
    if (isDefined(global.process?.versions?.node))
        return { global, type: CONTEXT.NodeJS };
    return { global, type: CONTEXT.Unknown };
};
/** Shared empty WeakSet sentinel to avoid allocations for default skip parameter */
const EMPTY_SKIP = new WeakSet();
export function deepFreeze(obj, options, seen = new WeakSet()) {
    // Support both old and new signatures for backward compatibility
    const skip = (options instanceof WeakSet) ? options : (options?.skip ?? EMPTY_SKIP);
    if (isPrimitive(obj) || Object.isFrozen(obj) || seen.has(obj) || skip.has(obj))
        return obj;
    if (obj?.[Symbol.toStringTag] === 'Enumify')
        return obj;
    if (obj[sym.$Extensible])
        return obj;
    seen.add(obj);
    ownEntries(obj).forEach(([key, val]) => {
        if (key !== '__proto__' && key !== 'constructor' && key !== 'prototype')
            deepFreeze(val, { skip }, seen);
    });
    return Object.freeze(obj);
}
